import { type ReactNode, useMemo } from 'react';

type EllipsisKey = 'start' | 'end';

interface UsePaginationArguments {
  currentPageIndex: number;
  totalPages: number;
  maxNumOfPageButtons?: number;
  showFirstLastButtons?: boolean;
  render: (index: number) => ReactNode;
  renderEllipsis?: (key: EllipsisKey) => ReactNode;
}

export const usePaginationRange = (args: UsePaginationArguments) => {
  const {
    currentPageIndex,
    totalPages,
    maxNumOfPageButtons = 7,
    showFirstLastButtons = false,
    render,
    renderEllipsis,
  } = args;

  /*
   * Memoize on the destructured primitives + render callbacks rather than
   * on the `args` object — callers invoke this with an inline object
   * literal, so depending on `[args]` would invalidate the memo on every
   * render and produce a fresh `Array<ReactNode>` reference.
   */
  return useMemo(() => {
    if (showFirstLastButtons && renderEllipsis) {
      return buildFirstLastRange({
        currentPageIndex,
        totalPages,
        maxNumOfPageButtons,
        render,
        renderEllipsis,
      });
    }

    return buildContiguousRange({
      currentPageIndex,
      totalPages,
      maxNumOfPageButtons,
      render,
    });
  }, [
    currentPageIndex,
    totalPages,
    maxNumOfPageButtons,
    showFirstLastButtons,
    render,
    renderEllipsis,
  ]);
};

interface BuildContiguousArgs {
  currentPageIndex: number;
  totalPages: number;
  maxNumOfPageButtons: number;
  render: (index: number) => ReactNode;
}

const buildContiguousRange = ({
  currentPageIndex,
  totalPages,
  maxNumOfPageButtons,
  render,
}: BuildContiguousArgs) => {
  const items: Array<ReactNode> = [];

  /**
   * Inititally, try to show 3 pages above and below the current page.
   * This helps gravitate towards showing "current" page in the center
   * of 7 page buttons.
   */
  const prevNextCount = Math.floor(maxNumOfPageButtons / 2);

  // Show max of 7 page buttons
  const targetRange = Math.min(maxNumOfPageButtons, totalPages);

  /**
   * Initial guesses
   *
   * Try to set the min page index to 3 below the current.
   * Try to set the max page index to 3 above the current.
   */
  let min = Math.max(0, currentPageIndex - prevNextCount);
  let max = Math.min(currentPageIndex + prevNextCount, totalPages - 1);

  /**
   * Pad the left
   *
   * If min index is not the first page
   * and we still don't have 7 page buttons,
   * decrease the min index so that we show more page buttons.
   */
  if (min > 0 && max - min < targetRange - 1) {
    min -= targetRange - (max - min) - 1;
  }

  /**
   * Pad the right
   *
   * If max index is not the last page
   * and we still don't have 7 page buttons,
   * increase the max index so that we show more page buttons.
   */
  if (max < totalPages - 1 && max - min < targetRange - 1) {
    max += targetRange - (max - min) - 1;
  }

  for (let i = min; i <= max; i += 1) {
    items.push(render(i));
  }

  return items;
};

interface BuildFirstLastArgs {
  currentPageIndex: number;
  totalPages: number;
  maxNumOfPageButtons: number;
  render: (index: number) => ReactNode;
  renderEllipsis: (key: EllipsisKey) => ReactNode;
}

/**
 * Range builder that keeps the first and last page buttons persistent,
 * separating them from the current page range with ellipses.
 *
 * - siblingCount: how many pages to show on each side of the current page
 *   (siblingCount = 2 gives 5 middle pages incl. current).
 * - When the range is adjacent to first/last, the ellipsis is skipped so
 *   consecutive pages render (dynamic width).
 */
const buildFirstLastRange = ({
  currentPageIndex,
  totalPages,
  maxNumOfPageButtons,
  render,
  renderEllipsis,
}: BuildFirstLastArgs) => {
  if (totalPages <= maxNumOfPageButtons) {
    const items: Array<ReactNode> = [];
    for (let i = 0; i < totalPages; i += 1) {
      items.push(render(i));
    }
    return items;
  }

  const lastIndex = totalPages - 1;

  /**
   * With first/last reserved, the center range has `maxNumOfPageButtons - 2`
   * slots. Half of the remainder (minus current) hits each side of the
   * current page.
   */
  const siblingCount = Math.floor((maxNumOfPageButtons - 2 - 1) / 2);

  const leftSibling = Math.max(currentPageIndex - siblingCount, 1);
  const rightSibling = Math.min(currentPageIndex + siblingCount, lastIndex - 1);

  const showStartEllipsis = leftSibling > 1;
  const showEndEllipsis = rightSibling < lastIndex - 1;

  // Near start: no left ellipsis. Extend the right side to maintain maxNumOfPageButtons active pages.
  if (!showStartEllipsis && showEndEllipsis) {
    const leftItemCount = maxNumOfPageButtons - 1;
    const items: Array<ReactNode> = [];
    for (let i = 0; i < leftItemCount; i += 1) {
      items.push(render(i));
    }
    items.push(renderEllipsis('end'));
    items.push(render(lastIndex));
    return items;
  }

  // Near end: no right ellipsis. Extend the left side to maintain maxNumOfPageButtons active pages.
  if (showStartEllipsis && !showEndEllipsis) {
    const rightItemCount = maxNumOfPageButtons - 1;
    const items: Array<ReactNode> = [];
    items.push(render(0));
    items.push(renderEllipsis('start'));
    for (let i = totalPages - rightItemCount; i <= lastIndex; i += 1) {
      items.push(render(i));
    }
    return items;
  }

  // Middle: first + left ellipsis + center range + right ellipsis + last.
  const items: Array<ReactNode> = [];
  items.push(render(0));
  items.push(renderEllipsis('start'));
  for (let i = leftSibling; i <= rightSibling; i += 1) {
    items.push(render(i));
  }
  items.push(renderEllipsis('end'));
  items.push(render(lastIndex));
  return items;
};
